<template>
  <!-- Nested in another page layout (an invoice inside a work's invoicing
       tab): only a title bar and the content, the parent draws the frame. -->
  <div v-if="nested" class="flex flex-col gap-4">
    <div
      v-if="$slots.cta || withBackButton || title?.length"
      class="flex flex-wrap items-center justify-between gap-3"
    >
      <div class="flex min-w-0 items-center gap-x-3">
        <FontAwesomeIcon v-if="icon" :icon="['fal', icon]" size="lg" />
        <h3 class="truncate text-xl font-bold capitalize-first">
          {{ title }}
        </h3>
      </div>
      <div class="ml-auto flex gap-x-1" :class="ctaClass">
        <slot name="cta" />
        <BaseButton
          v-if="withBackButton"
          color="dark"
          icon="long-arrow-left"
          :title="$t('button.back')"
          @click="goBack"
        />
      </div>
    </div>
    <slot name="before_scroll_container" />
    <slot />
    <div v-if="$slots.footer" class="flex flex-row justify-end gap-x-3">
      <slot name="footer" />
    </div>
  </div>
  <div
    v-else
    id="work_layout"
    class="box relative flex h-full flex-col shadow-md sm:mx-5"
  >
    <div
      v-if="$slots.cta || withBackButton || title?.length"
      class="sticky top-0 right-0 left-0 z-50"
    >
      <div
        class="flex flex-wrap items-center justify-between gap-3 rounded-t-md border-b bg-white/90 p-3 dark:bg-slate-700/90"
      >
        <div
          class="flex min-w-32 flex-1 gap-x-3 sm:min-w-0 sm:flex-none sm:justify-center"
        >
          <div class="flex min-w-0 items-center gap-x-3">
            <FontAwesomeIcon v-if="icon" :icon="['fal', icon]" size="xl" />
            <h2
              class="truncate text-2xl font-bold capitalize-first max-sm:text-xl"
            >
              {{ title }}
            </h2>
          </div>
        </div>
        <div class="ml-auto flex shrink-0 justify-center">
          <div class="flex gap-x-1" :class="ctaClass">
            <slot name="cta" />
            <BaseButton
              v-if="withBackButton"
              color="dark"
              icon="long-arrow-left"
              :title="$t('button.back')"
              @click="goBack"
            />
          </div>
        </div>
      </div>
    </div>
    <div>
      <slot name="before_scroll_container" />
    </div>
    <div
      id="work_layout_container"
      class="min-h-0 flex-1 bg-gray-50 dark:border-slate-700 dark:bg-slate-700"
      :class="[
        notScrollable ? 'overflow-hidden' : 'overflow-y-auto rounded-b-md'
      ]"
    >
      <div :class="[padded ? 'flex flex-col gap-4 p-4' : '']">
        <slot />
      </div>
    </div>
    <div
      v-if="$slots.footer"
      class="sticky right-0 bottom-0 left-0 z-50 flex flex-row items-center gap-x-3 rounded-b-md border-t bg-white/90 dark:bg-slate-700/90"
    >
      <div class="flex flex-1 flex-row justify-end gap-x-3 p-3">
        <slot name="footer" />
      </div>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'

/**
 * Page frame of a work ("dossier") tab: Funeral Manager's
 * BaseDossierPageLayout without its dossier-specific logic (FM dossier store,
 * display modes, previous/next navigation). Each tab keeps its own edition
 * mode and puts its buttons in the `cta` (header) or `footer` slot.
 *
 * Nested inside another BaseWorkPageLayout (pages shared between the
 * invoicing module and a work's invoicing tab), it renders bare.
 */
export default {
  name: 'BaseWorkPageLayout',
  components: { BaseButton, FontAwesomeIcon },
  provide() {
    return { insidePageLayout: true }
  },
  inject: {
    insidePageLayout: { default: false }
  },
  props: {
    title: { type: String, required: false, default: null },
    icon: { type: String, required: false, default: null },
    ctaClass: { type: String, required: false, default: '' },
    withBackButton: { type: Boolean, required: false, default: false },
    backRoute: { type: String, required: false, default: null },
    notScrollable: { type: Boolean, required: false, default: false },
    /** Pads the content and spaces its sections, as FM's dossier pages do. */
    padded: { type: Boolean, required: false, default: true }
  },
  computed: {
    nested() {
      return this.insidePageLayout
    }
  },
  methods: {
    goBack() {
      if (this.backRoute) {
        this.$router.push({ name: this.backRoute })
      } else {
        this.$router.go(-1)
      }
    }
  }
}
</script>
